// Page-curl geometry for the flipbook.
//
// Everything is solved in a "canonical" frame: the turning sheet lies flat at
// X in [0, W], Y in [0, H] with the spine at X = 0, and its free corner C is
// dragged to a point P. The fold is the perpendicular bisector of C and P:
//   - the part of the sheet on the spine side stays flat (front face visible),
//   - the part on the corner side is reflected across the fold (back face visible),
//   - where the sheet has lifted away, the page underneath shows through.
//
// Backward flips (turning a left-hand page) are the same problem mirrored in X;
// `dir` = +1 / -1 converts canonical X to book X. The caller maps book X to
// pixels with `spineX` (the spine's x offset inside the book element).
//
// Results are CSS-ready: clip-path polygons in each element's own local box, and
// matrix() transforms that place the back face, so page content stays live DOM
// (canvas + overlays) the whole way through the turn.

const EPS = 0.5;

const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a, b) => a.x * b.x + a.y * b.y;
const len = (a) => Math.hypot(a.x, a.y);

// Keep the corner where a real sheet could put it: no further from either
// spine end than the sheet's edge/diagonal allows (otherwise it would tear).
export const constrainCorner = (P, corner, W, H) => {
    const near = corner === 'top' ? { x: 0, y: 0 } : { x: 0, y: H };
    const far = corner === 'top' ? { x: 0, y: H } : { x: 0, y: 0 };
    const diag = Math.hypot(W, H);
    let p = { ...P };
    const clampTo = (centre, r) => {
        const d = sub(p, centre);
        const l = len(d);
        if (l > r) p = { x: centre.x + (d.x / l) * r, y: centre.y + (d.y / l) * r };
    };
    clampTo(near, W);
    clampTo(far, diag);
    return p;
};

export const cornerPoint = (corner, W, H) => ({ x: W, y: corner === 'top' ? 0 : H });

// Sutherland–Hodgman against one half-plane: keeps points where side(pt) <= 0.
const clipHalfPlane = (poly, side) => {
    const out = [];
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        const sa = side(a);
        const sb = side(b);
        if (sa <= 0) out.push(a);
        if ((sa < 0 && sb > 0) || (sa > 0 && sb < 0)) {
            const t = sa / (sa - sb);
            out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
        }
    }
    return out;
};

export const polygonCss = (poly) =>
    poly.length < 3
        ? 'polygon(0 0, 0 0, 0 0)'
        : `polygon(${poly.map((p) => `${p.x.toFixed(2)}px ${p.y.toFixed(2)}px`).join(', ')})`;

// 2D affine as { a, b, c, d, e, f } with CSS matrix() semantics:
// x' = a x + c y + e, y' = b x + d y + f.
export const matrixCss = (m) =>
    `matrix(${[m.a, m.b, m.c, m.d, m.e, m.f].map((v) => v.toFixed(5)).join(',')})`;

// Front face local u -> canonical X. Forward: the right page, u = X.
// Backward: the left page, whose spine edge is its right edge, so X = W - u.
const frontToCanon = (dir, W) => (dir > 0 ? { a: 1, e: 0 } : { a: -1, e: W });
// Back face local u -> canonical sheet X. The back of a right-hand sheet is a
// left-hand page (spine at its right edge): X = W - u. And vice versa.
const backToCanon = (dir, W) => (dir > 0 ? { a: -1, e: W } : { a: 1, e: 0 });
const invX = (m, p) => ({ x: (p.x - m.e) / m.a, y: p.y });

/**
 * Solve one frame of a page turn.
 *
 * @param {object} o
 * @param {number} o.W, o.H     page size in px
 * @param {number} o.dir        +1 turning a right-hand page leftwards, -1 the reverse
 * @param {'top'|'bottom'} o.corner
 * @param {{x,y}} o.P           where the corner is, canonical frame (already constrained)
 * @param {number} o.spineX     spine x inside the book element, px
 * @returns null when the sheet is flat, else polygons/transforms in px.
 */
export const solveFold = ({ W, H, dir, corner, P, spineX }) => {
    const C = cornerPoint(corner, W, H);
    const CP = sub(C, P);
    const dist = len(CP);
    if (dist < EPS) return null;

    // Fold line: through midpoint M, unit normal n pointing toward the corner side.
    const n = { x: CP.x / dist, y: CP.y / dist };
    const M = { x: (C.x + P.x) / 2, y: (C.y + P.y) / 2 };
    const side = (pt) => dot(sub(pt, M), n); // > 0 on the corner side

    const sheet = [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W, y: H }, { x: 0, y: H }];
    const flat = clipHalfPlane(sheet, side); // still lying on the book
    const lifted = clipHalfPlane(sheet, (pt) => -side(pt)); // folded over

    const reflect = (pt) => {
        const k = 2 * side(pt);
        return { x: pt.x - k * n.x, y: pt.y - k * n.y };
    };
    const toPx = (pt) => ({ x: spineX + dir * pt.x, y: pt.y });

    const f = frontToCanon(dir, W);
    const b = backToCanon(dir, W);

    // Back face: local (u, y) -> canonical sheet -> reflected -> book px.
    // Built by mapping three basis points; it's affine end to end.
    const map = (u, y) => toPx(reflect({ x: b.a * u + b.e, y }));
    const o = map(0, 0);
    const ex = map(1, 0);
    const ey = map(0, 1);
    const backMatrix = { a: ex.x - o.x, b: ex.y - o.y, c: ey.x - o.x, d: ey.y - o.y, e: o.x, f: o.y };

    // Fold line endpoints across the sheet, in px — used to lay shadow strips.
    const foldPts = flat.filter((pt) => Math.abs(side(pt)) < 1e-6).map(toPx);

    return {
        // How far through the turn we are, 0 (flat) .. 1 (fully over).
        progress: Math.min(1, dist / (2 * W)),
        frontClip: flat.map((pt) => invX(f, pt)),
        backClip: lifted.map((pt) => invX(b, pt)),
        backMatrix,
        // Regions in book px (for shadows layered above/below the sheet).
        flatPx: flat.map(toPx),
        liftedPx: lifted.map(reflect).map(toPx),
        uncoveredPx: lifted.map(toPx),
        foldMidPx: toPx(M),
        foldPts,
        // Normal toward the corner side, in px space.
        normalPx: { x: dir * n.x, y: n.y },
    };
};
